import React from "react";
import { graphql } from "gatsby";
import ReactMarkdown from "react-markdown";
import Button, { Rank } from "@unum/button";
import Heading from "@unum/heading";
import Hero from "@unum/hero";
import Section from "@unum/section";
import TableOfContents from "@unum/table-of-contents";
import LozengeNav from "@unum/lozenge-nav";
import { GetPageQuery } from "../../graphql-types";
import FeaturedAsset from "../components/FeaturedAsset";
import Layout, { Location } from "../components/Layout";
import { getClickableAction } from "../components/Link";
import PreFooterSection from "../components/PreFooterSection";
import RichText from "../components/RichText";
import Sections from "../components/Sections";
import renderSpotlightContent from "../utils/render-spotlight-content";
import { ShareImages } from "../utils/meta-open-graph";
import pageConfig from "../utils/page-config";
import { BreadcrumbItem } from "@unum/breadcrumb";

type Props = {
  data: GetPageQuery;
  location: Location;
  pageContext: {
    id: string;
    breadcrumbItems: BreadcrumbItem[];
  };
};

const heroTypeToLayout: Record<
  GetPageQuery["page"]["elements"]["hero_type"]["value"][0]["codename"],
  | "no-hero"
  | "curve"
  | "two-column"
  | "two-column-asset"
  | "campaign-left"
  | "campaign-right"
> = {
  curve: "curve",
  two_column: "two-column",
  two_column_asset: "two-column-asset",
  no_hero: "no-hero",
  no_image: "two-column-asset",
  campaign_left: "campaign-left",
  campaign_right: "campaign-right"
};

const heroLayoutToButtonRankMap: Record<
  | "curve"
  | "two-column"
  | "two-column-asset"
  | "campaign-left"
  | "campaign-right",
  Rank
> = {
  curve: "tertiary",
  "two-column": "secondary",
  "two-column-asset": "secondary",
  "campaign-left": "primary",
  "campaign-right": "primary"
};

const Page = ({ data, location, pageContext }: Props) => {
  const {
    title,
    subtitle,
    hero_type,
    ctas,
    sections,
    branch,
    pre_footer_content,
    footer_text,
    metadata_description,
    settings
  } = data.page.elements;
  const featured_asset = data.page.elements.featured_asset.value[0];
  const { help_section } = data.site?.elements || {};
  const heroLayout =
    heroTypeToLayout[hero_type.value[0]?.codename] || "two-column-asset";

  const { isStandaloneHeader, isStandaloneFooter, noIndex } = pageConfig(
    settings.value
  );

  const shareImages = {
    featured: featured_asset.elements as ShareImages["featured"],
    meta: data.page.elements.social_sharing_images.value as ShareImages["meta"]
  };

  const hasHero = heroLayout !== "no-hero";
  const ContentWrapper = hasHero ? Heading.Content : React.Fragment;

  const { breadcrumbItems } = pageContext;

  return (
    <Layout
      title={title.value}
      metaTitle={data.page.elements.meta_override_title.value}
      description={metadata_description?.value || subtitle.value}
      location={location}
      branch={branch.value[0]}
      footerText={footer_text}
      data-kontent-item-id={data.page.system.id}
      hasNoIndex={noIndex}
      isStandaloneHeader={isStandaloneHeader}
      isStandaloneFooter={isStandaloneFooter}
      shareImages={shareImages}
      breadCrumbItems={breadcrumbItems}
    >
      {hasHero && (
        <Hero
          title={renderSpotlightContent(
            // NOTE: we don't want a p element inside h1
            <ReactMarkdown disallowedElements={["p"]} unwrapDisallowed>
              {title.value}
            </ReactMarkdown>,
            "title"
          )}
          asset={
            hero_type.value[0]?.codename !== "no_image" && (
              <FeaturedAsset
                data={featured_asset}
                showPreview={heroLayout === "curve"}
                type="hero"
              />
            )
          }
          layout={heroLayout}
          ctas={ctas?.value.map((cta, index) => {
            return (
              <Button
                key={cta.id}
                action={getClickableAction(cta)}
                rank={heroLayoutToButtonRankMap[heroLayout]}
                theme={index === 0 ? "default" : "ghost"}
              >
                {/* @ts-expect-error As per other examples throughout codebase */}
                {cta.elements.label.value}
              </Button>
            );
          })}
        >
          {renderSpotlightContent(<RichText data={subtitle} />, "subtitle")}
        </Hero>
      )}

      <ContentWrapper>
        <TableOfContents>
          <TableOfContents.Menu>
            {(headings) => {
              if (headings.length > 1) {
                return (
                  <Section spacing="top">
                    <LozengeNav
                      links={headings.map((heading) => ({
                        ariaLabel: heading.title,
                        action: { model: "htmlLink", href: `#${heading.id}` },
                        active: false
                      }))}
                    />
                  </Section>
                );
              }
            }}
          </TableOfContents.Menu>
          <Sections data={sections.value} />
          <PreFooterSection
            data={help_section.value[0]}
            type={pre_footer_content.value[0]?.codename}
          />
        </TableOfContents>
      </ContentWrapper>
    </Layout>
  );
};

export default Page;

export const query = graphql`
  query getPage($id: String!) {
    page: kontentItemPage(id: { eq: $id }) {
      id
      system {
        id
      }
      elements {
        title {
          value
        }
        featured_asset {
          value {
            ...ExternalVideoFragment
            ...ImageFragment
          }
        }
        subtitle {
          ...RichTextFragment
        }
        ctas {
          value {
            id
            ...LinkFragment
          }
        }
        hero_type {
          value {
            name
            codename
          }
        }
        sections {
          value {
            ...SectionsFragment
          }
        }
        branch {
          value {
            ...BranchFragment
          }
        }
        pre_footer_content {
          value {
            codename
          }
        }
        footer_text {
          value
        }
        meta_override_title {
          value
        }
        metadata_description {
          value
        }
        social_sharing_images {
          value {
            ... on kontent_item_asset {
              elements {
                image {
                  value {
                    type
                    url
                  }
                }
              }
            }
          }
        }
        settings {
          value {
            codename
          }
        }
      }
    }
    site: kontentItemSite {
      elements {
        help_section {
          value {
            ...TitleWithContentFragment
          }
        }
      }
    }
  }
`;
